/**
 * ============================================================================
 * i18n Configuration - Internacionalização
 * ============================================================================
 *
 * Configuração do i18next para suporte multilíngue:
 * - Idiomas suportados: pt-BR, pt-PT, es
 * - Detecção automática do idioma do navegador
 * - Persistência em localStorage
 * - Carregamento dinâmico de traduções por namespace
 * - Fallback para pt-BR
 *
 * ESTRUTURA:
 * - /locales/{locale}/{namespace}.json
 * - Namespaces: common, auth, dashboard, meetings, etc.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Importar traduções
import ptBRCommon from '@/locales/pt-BR/common.json';
import ptBRAuth from '@/locales/pt-BR/auth.json';
import ptBRDocs from '@/locales/pt-BR/docs.json';

import ptPTCommon from '@/locales/pt-PT/common.json';
import ptPTAuth from '@/locales/pt-PT/auth.json';
import ptPTDocs from '@/locales/pt-PT/docs.json';

import esCommon from '@/locales/es/common.json';
import esAuth from '@/locales/es/auth.json';
import esDocs from '@/locales/es/docs.json';

import enCommon from '@/locales/en/common.json';
import enAuth from '@/locales/en/auth.json';
import enDocs from '@/locales/en/docs.json';

// Idiomas suportados
export const SUPPORTED_LANGUAGES = {
  en: { name: 'English', flag: '🇺🇸', nativeName: 'English' },
  'pt-BR': { name: 'Português (Brasil)', flag: '🇧🇷', nativeName: 'Português (BR)' },
  'pt-PT': { name: 'Português (Portugal)', flag: '🇵🇹', nativeName: 'Português (PT)' },
  es: { name: 'Español', flag: '🇪🇸', nativeName: 'Español' },
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

// Idioma padrão
export const DEFAULT_LANGUAGE: SupportedLanguage = 'pt-PT';

// Storage key para persistência do idioma
export const LANGUAGE_STORAGE_KEY = 'stant:language';

// Recursos de tradução
const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    docs: enDocs,
  },
  'pt-BR': {
    common: ptBRCommon,
    auth: ptBRAuth,
    docs: ptBRDocs,
  },
  'pt-PT': {
    common: ptPTCommon,
    auth: ptPTAuth,
    docs: ptPTDocs,
  },
  es: {
    common: esCommon,
    auth: esAuth,
    docs: esDocs,
  },
};

// Configuração do detector de idioma
const languageDetectorOptions = {
  order: ['localStorage', 'navigator', 'htmlTag'],
  lookupLocalStorage: LANGUAGE_STORAGE_KEY,
  caches: ['localStorage'],
  excludeCacheFor: ['cimode'],
};

// Inicializar i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: Object.keys(SUPPORTED_LANGUAGES),
    defaultNS: 'common',
    ns: ['common', 'auth', 'docs'],

    // Detecção de idioma
    detection: languageDetectorOptions,

    // Interpolação
    interpolation: {
      escapeValue: false, // React já faz escaping
    },

    // React options
    react: {
      useSuspense: false, // Desabilitar suspense para evitar flicker na primeira renderização
      bindI18n: 'languageChanged loaded', // Re-render quando o idioma muda
      bindI18nStore: 'added removed', // Re-render quando recursos são adicionados/removidos
    },

    // Debug (apenas em desenvolvimento)
    debug: import.meta.env.DEV,
  });

export default i18n;
